/**
 * @file Renders one tier amount cell of the transfer table, showing custom, replaced or combined amounts.
 */
import { Typography } from "antd";
import type { CSSProperties } from "react";

import {
  getCurrencyValue,
  getInvestmentTierAmount,
  getInvestmentTierCurrency,
} from "./helpers/getInvestedAmount";
import type { Investment, CustomAmountsMap } from "./types";

const COLOR_CUSTOM = "#faad14";
const COLOR_CHANGED = "#52c41a";

const NOWRAP_STYLE: CSSProperties = { whiteSpace: "nowrap" };

const REPLACED_STYLE: CSSProperties = {
  whiteSpace: "nowrap",
  textDecoration: "line-through",
  opacity: 0.5,
};

type Props = {
  tierId: string;
  isIncluded: boolean;
  investment: Investment;
  customAmountsMap: CustomAmountsMap;
  sourceInvestments: Investment[];
  sourceInvestmentId?: string | null;
};

/**
 * Renders the tier amount for one investment row, reflecting its custom, source or combined amount.
 */
const TableAmountColumn = ({
  tierId,
  isIncluded,
  investment,
  customAmountsMap,
  sourceInvestments,
  sourceInvestmentId = undefined,
}: Props) => {
  /**
   * Finds the source investment with the given id.
   */
  const getSourceInvestment = (investmentId: string) =>
    sourceInvestments.find(({ id }) => id === investmentId);

  if (!isIncluded) {
    return (
      <Typography.Text type="secondary">
        <span style={NOWRAP_STYLE}>
          {getInvestmentTierCurrency(tierId, investment)}
        </span>
      </Typography.Text>
    );
  }

  const investmentCustomAmounts = customAmountsMap[investment.id];
  const hasCustomAmounts = !!investmentCustomAmounts;

  if (hasCustomAmounts) {
    const customAmount = investmentCustomAmounts[tierId];

    if (customAmount) {
      return (
        <Typography.Text strong style={{ color: COLOR_CUSTOM }}>
          <span style={NOWRAP_STYLE}>
            {getCurrencyValue(Number(customAmount))}
          </span>
        </Typography.Text>
      );
    }
  }

  const wrapperStyle = hasCustomAmounts
    ? REPLACED_STYLE
    : NOWRAP_STYLE;

  if (!sourceInvestmentId) {
    return (
      <span style={wrapperStyle}>
        {getInvestmentTierCurrency(tierId, investment)}
      </span>
    );
  }

  const sourceInvestment = getSourceInvestment(sourceInvestmentId);

  const amount = getInvestmentTierAmount(tierId, investment) || 0;
  const sourceAmount = getInvestmentTierAmount(tierId, sourceInvestment) || 0;

  const newAmount = amount + sourceAmount;
  const currencyValue = getCurrencyValue(newAmount);

  if (newAmount) {
    const isTargetAmountChanged =
      !!amount &&
      !!sourceAmount;

    return (
      <span style={wrapperStyle}>
        {
          isTargetAmountChanged
            ? <Typography.Text strong style={{ color: COLOR_CHANGED }}>{currencyValue}</Typography.Text>
            : currencyValue
        }
      </span>
    );
  }

  return null;
};

export default TableAmountColumn;
